/* eslint-disable react/prop-types */
import themeGet from "@styled-system/theme-get";
import { get, isUndefined } from "lodash";
import { Icon, P } from "orcs-design-system";
import PropTypes from "prop-types";
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  useContext,
} from "react";
import { Treebeard, decorators } from "react-treebeard";
import styled from "styled-components";

import * as placeholder from "src/components/Placeholder/PlaceholderStyles";
import { NOOP } from "src/util/consts";
import GroupTypeBadge from "src/components/GroupTypeBadge";

import { ThemeSwitchingContext } from "src/contexts/ThemeSwitchingContext";

import TreeViewStyle from "./TreeView.json";
import { updateTreeObject } from "./TreeView.util";

const TreeGroupExpandIconFlexWrapper = styled.button`
  border: none;
  background: none;
  appearance: none;
  z-index: 2;
  cursor: ${({ disabled }) => (disabled ? "default" : "pointer")};
  padding: 0;
  margin-right: ${themeGet("space.s")};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  flex-shrink: 0;
  width: 24px;
  text-align: center;
  transition: transform 200ms ease-in-out;
  transform: ${({ $isExpanded }) =>
    $isExpanded ? "rotate(90deg)" : "rotate(0deg)"};
  color: inherit;
`;

export const TreeHeaderTitle = styled.button`
  cursor: pointer;
  border: none;
  background: none;
  appearance: none;
  justify-content: space-between;
  font-family: inherit;
  z-index: 2;
  display: flex;
  align-items: center;
  font-size: ${themeGet("fontSizes.1")};
  font-weight: ${themeGet("fontWeights.1")};
  text-align: left;
  text-decoration: ${({ isHidden }) => (isHidden ? "line-through" : "none")};
  margin-left: 0;
  line-height: 1;
  padding: 0;
  padding-left: ${(props) =>
    props.hasChildren ? "0px" : `calc(24px + ${themeGet("space.s")(props)})`};
  min-width: 0;
  flex: 1;
  overflow: hidden;
`;

export const TreeHeaderStatus = styled.div`
  display: none;
`;

export const TreeHeader = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  flex: 1;
  justify-content: space-between;
  gap: ${themeGet("space.xxs")};
  border-radius: ${themeGet("radii.2")};
  padding: ${themeGet("space.xs")} ${themeGet("space.s")};
  margin: 0 -${themeGet("space.s")};
  transition: ${themeGet("transition.transitionDefault")};

  ${(props) =>
    props.isActive &&
    `
    background-color: ${themeGet("colors.primaryLightest")(props)};
  `}

  &:hover {
    background-color: ${(props) =>
      props.isActive
        ? themeGet("colors.primaryLightest")(props)
        : themeGet("colors.hoverSurface")(props)};
  }
`;

export const TreeText = styled(P)`
  word-break: break-word;
  z-index: 2;
  transition: ${themeGet("transition.transitionDefault")};
  color: ${(props) =>
    props.isActive
      ? themeGet("colors.primary")(props)
      : themeGet("colors.bodyText")(props)};

  ${TreeHeader}:hover & {
    color: ${(props) => themeGet("colors.primary")(props)};
  }
`;

const ActiveTreeNodeProps = {
  id: "active-node",
  "data-testid": "active-node-test",
};

class TreeContainer extends decorators.Container {
  renderToggleDecorator() {
    const { style, node } = this.props;
    return <Toggle style={style.toggle} node={node} />;
  }
}

const Toggle = ({ node }) => {
  // Hide toggle for nodes that don't have children (leaf nodes)
  // This will hide toggles for direct child tags
  const shouldShowToggle = node.children && node.children.length > 0;

  return (
    <TreeGroupExpandIconFlexWrapper
      aria-expanded={node.toggled ? "true" : "false"}
      aria-label={node.toggled ? "Collapse tree node" : "Expand tree node"}
      flexDirection="column"
      alignItems="center"
      className="treeExpandIcon"
      display={shouldShowToggle ? "flex" : "none"}
      disabled={node.disabled}
    >
      <Icon icon={["fal", "chevron-right"]} size="1x" color="bodyText" />
    </TreeGroupExpandIconFlexWrapper>
  );
};

const Loading = () => {
  return <placeholder.Line width={250} height={24} />;
};

export const DefaultTreeHeader = ({
  nodeStatus,
  nodeIcon,
  onActivate,
  onToggle,
  cursor,
  withGroupTypeBadge,
  flipOnClickHeader,
  groupTypes,
  node,
}) => {
  useEffect(() => {
    if (node.active) {
      cursor.current = node;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { group: treeNodeGroup } = node;
  const hasChildren = Boolean(node.children);

  const activeNodeProps = node.active ? ActiveTreeNodeProps : {};

  const onClickHeader = (e) => {
    e.stopPropagation();
    if (flipOnClickHeader && hasChildren) {
      onToggle(node, !node.toggled);
    } else {
      onActivate(node);
    }
  };

  return (
    <TreeHeader
      hasChildren={hasChildren}
      onClick={onClickHeader}
      isActive={node.active}
    >
      <TreeHeaderTitle
        role="link"
        className="treeHeaderTitle"
        data-testid={node.active ? "treeheader-node-bold" : "treeheader-node"}
        isHidden={get(node, "group.isHidden")}
        hasChildren={hasChildren}
        style={{ minWidth: 0 }}
      >
        <TreeText
          fontWeight="1"
          data-testid="treeheader-node-name"
          color={node.isPlaceholder ? "greyDark" : undefined}
          fontSize="1"
          isActive={node.active}
          {...activeNodeProps}
        >
          {node.name}
        </TreeText>
        <TreeHeaderStatus className="treeHeaderStatus">
          {nodeStatus(node)}
        </TreeHeaderStatus>
      </TreeHeaderTitle>
      {withGroupTypeBadge && treeNodeGroup && (
        <GroupTypeBadge group={treeNodeGroup} groupTypes={groupTypes} />
      )}
      {nodeIcon(node)}
    </TreeHeader>
  );
};

const buildHeader = (props) => {
  if (props.customizeTreeHeader) {
    const HeaderComponent = props.customizeTreeHeader(props);
    if (!isUndefined(HeaderComponent)) {
      return function WrappedCustomHeader({ node, ...rest }) {
        return (
          <HeaderComponent
            node={node}
            loadingNodeIds={props.loadingNodeIds}
            {...rest}
          />
        );
      };
    }
  }

  return function WrappedTreeHeader({ node }) {
    return <DefaultTreeHeader {...props} node={node} />;
  };
};

const TreeView = ({
  data,
  "data-testid": testId,
  className,
  onNodeSelected = NOOP,
  onNodeToggled = NOOP,
  nodeStatus = NOOP,
  nodeIcon = NOOP,
  customizeTreeHeader,
  withGroupTypeBadge = false,
  flipOnClickHeader = false,
  groupTypes,
  shouldIgnoreOnToggle = null,
  loadingNodeIds = {},
}) => {
  const themeSwitchingCtx = useContext(ThemeSwitchingContext);
  const treeViewStyle = useMemo(() => {
    const bodyTextColor = themeSwitchingCtx?.theme?.colors?.bodyText;
    if (!bodyTextColor) return TreeViewStyle;
    return {
      ...TreeViewStyle,
      tree: {
        ...TreeViewStyle.tree,
        base: { ...TreeViewStyle.tree.base, color: bodyTextColor },
      },
    };
  }, [themeSwitchingCtx?.theme?.colors?.bodyText]);

  const [treeData, setTreeData] = useState(data);
  const cursor = useRef();

  useEffect(() => {
    setTreeData(data);
    cursor.current = undefined;
  }, [data]);

  useEffect(() => {
    TreeView.scrollActiveNodeIntoView();
  }, []);

  const refreshTree = useCallback(() => {
    setTreeData((td) => updateTreeObject(td));
  }, []);

  // onToggle is used for expanding nodes
  const onToggle = useCallback(
    (node, toggled) => {
      if (shouldIgnoreOnToggle && shouldIgnoreOnToggle(node)) {
        return;
      }
      // If not doesn't want to be mutated
      if (node.noActivation) {
        return;
      }

      if (node.children) {
        // toggle it if it has children
        node.toggled = toggled;
      }

      // refresh the render
      refreshTree();

      // Callback for node toggled
      onNodeToggled(node, refreshTree);
    },
    [shouldIgnoreOnToggle, refreshTree, onNodeToggled]
  );

  // onActivate is used on leaf node clicks
  const onActivate = useCallback(
    (node) => {
      if (cursor.current) {
        // reset the old node to inactive
        cursor.current.active = false;
      }

      if (node.noActivation) {
        return;
      }

      // set the new node to active
      node.active = true;

      // track the cursor/active node
      cursor.current = node;

      // refresh the render
      refreshTree();

      // Callback for node active
      onNodeSelected(node, refreshTree);
    },
    [onNodeSelected, refreshTree]
  );

  const Header = useMemo(
    () =>
      buildHeader({
        nodeStatus,
        nodeIcon,
        onActivate,
        onToggle,
        refreshTree,
        cursor,
        customizeTreeHeader,
        withGroupTypeBadge,
        flipOnClickHeader,
        groupTypes,
        loadingNodeIds,
      }),
    [
      nodeStatus,
      nodeIcon,
      onActivate,
      onToggle,
      refreshTree,
      customizeTreeHeader,
      withGroupTypeBadge,
      flipOnClickHeader,
      groupTypes,
      loadingNodeIds,
    ]
  );

  return (
    <div data-testid={testId}>
      <Treebeard
        animations={false}
        className={className}
        data={treeData}
        style={treeViewStyle}
        onToggle={onToggle}
        decorators={{
          ...decorators,
          Header,
          Toggle,
          Loading,
          Container: TreeContainer,
        }}
      />
    </div>
  );
};

TreeView.propTypes = {
  data: PropTypes.oneOfType([PropTypes.object, PropTypes.array]).isRequired,
  "data-testid": PropTypes.string,
  className: PropTypes.string,
  onNodeSelected: PropTypes.func,
  onNodeToggled: PropTypes.func,
  nodeStatus: PropTypes.func,
  nodeIcon: PropTypes.func,
  customizeTreeHeader: PropTypes.func,
  flipOnClickHeader: PropTypes.bool,
  groupTypes: PropTypes.object,
  loadingNodeIds: PropTypes.object,
};

TreeView.scrollActiveNodeIntoView = (block = "end") => {
  const elem = document.querySelector("#active-node");
  if (elem && elem.scrollIntoView) {
    elem.scrollIntoView({ behavior: "smooth", block });
  }
};

export default TreeView;
